import { ExpandedState } from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { AnalysisCommand, Order } from '../../data-access/types/orders';

import { DateHeader } from './date-header';
import { TimelineBar } from './timeline-bar';
import classes from './timeline-view.module.css';
import { TableRow } from './types';
import {
  DAY_WIDTH,
  generateDayGridLines,
  generateWeekendDays,
  getDayOffsetFromMinDate,
  WEEK_WIDTH,
} from './utils';

interface TimelineViewProps {
  orders: Order[];
  weekHeaders: Date[];
  totalWeeks: number;
  minDate: Date;
  currentWeekOffset: number;
  headerScrollRef: React.RefObject<HTMLDivElement>;
  rightScrollRef: React.RefObject<HTMLDivElement>;
  expandedState: ExpandedState;
  showDayGrid?: boolean;
  onVerticalScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  footerHeight?: number;
}

export function TimelineView({
  orders,
  weekHeaders,
  totalWeeks,
  minDate,
  currentWeekOffset,
  headerScrollRef,
  rightScrollRef,
  expandedState,
  onVerticalScroll,
  showDayGrid = false,
  footerHeight = 0,
}: TimelineViewProps) {
  const tableRows = orders.flatMap((order) => [
    order,
    ...order.analysis_commands.filter((item) => {
      if (expandedState === true) return true;

      return expandedState[order.id.toString()];
    }),
  ]);

  // Generate day grid for debugging
  const dayGridLines = generateDayGridLines(
    minDate,
    new Date(minDate.getTime() + totalWeeks * 7 * 24 * 60 * 60 * 1000),
  );

  // Generate weekend days for gray highlighting
  const weekendDays = generateWeekendDays(
    minDate,
    new Date(minDate.getTime() + totalWeeks * 7 * 24 * 60 * 60 * 1000),
  );

  const renderTimelineRow = (row: TableRow) => {
    const weekendElements = (
      <div className={classes.weekendBackground}>
        {weekendDays.map((weekendDay, index) => {
          const dayOffset = getDayOffsetFromMinDate(weekendDay, minDate);
          return (
            <div
              className={classes.weekendDay}
              key={`weekend-${row.id}-${index}`}
              style={{
                left: `${dayOffset * DAY_WIDTH}px`,
                width: `${DAY_WIDTH}px`,
              }}
            />
          );
        })}
      </div>
    );
    if (row.name) {
      const order = row as Order;
      // For parent rows, show all analysis command timelines
      return (
        <div
          className={`${classes.timelineRow} ${classes.parentTimelineRow}`}
          data-parent={row.id}
          key={row.id}
        >
          {/* Weekend days for this row */}
          {weekendElements}

          <div className={classes.timelineContainer}>
            {order.analysis_commands.flatMap((command) =>
              command.timelines.map((timeline, index) => (
                <TimelineBar
                  key={`${timeline.id}-${timeline.start_date}-${index}`}
                  minDate={minDate}
                  timeline={timeline}
                />
              )),
            )}
          </div>
        </div>
      );
    } else {
      const command = row as AnalysisCommand;
      // For child rows, show only the specific analysis command timelines
      return (
        <div
          className={`${classes.timelineRow} ${classes.childTimelineRow}`}
          key={row.id}
        >
          {/* Weekend days for this row */}
          {weekendElements}
          <div className={classes.timelineContainer}>
            {command.timelines.map((timeline, index) => (
              <TimelineBar
                key={`${timeline.id}-${timeline.start_date}-${index}`}
                minDate={minDate}
                timeline={timeline}
              />
            ))}
          </div>
        </div>
      );
    }
  };

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerScroll} ref={headerScrollRef}>
          <DateHeader totalWeeks={totalWeeks} weekHeaders={weekHeaders} />
        </div>
      </div>

      {/* Timeline Body */}
      <div
        className={classes.body}
        onScroll={onVerticalScroll}
        ref={rightScrollRef}
      >
        <div
          className={classes.bodyContent}
          style={{
            minWidth: `${totalWeeks * WEEK_WIDTH}px`,
            paddingBottom: `${footerHeight}px`,
          }}
        >
          {/* Day grid for debugging */}
          {showDayGrid ? (
            <div className={classes.dayGrid}>
              {dayGridLines.map((day, index) => {
                const dayOffset = getDayOffsetFromMinDate(day, minDate);
                return (
                  <div
                    className={classes.dayGridLine}
                    key={`day-${index}`}
                    style={{
                      left: `${dayOffset * DAY_WIDTH}px`,
                    }}
                  >
                    <span className={classes.dayGridLabel}>
                      {day.getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* Current week line */}
          {currentWeekOffset >= 0 && currentWeekOffset < totalWeeks && (
            <div
              className={classes.currentDateLine}
              style={{
                left: `${currentWeekOffset * WEEK_WIDTH + WEEK_WIDTH / 2}px`,
              }}
            />
          )}

          {/* Timeline rows */}
          {tableRows.map(renderTimelineRow)}
          {/* <div
            style={{
              height: '56.5px',
            }}
          /> */}
        </div>
      </div>
    </div>
  );
}
